import { useState } from "react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { LoadingState } from "../components/common/LoadingState";
import { ErrorState } from "../components/common/ErrorState";
import { StatCard } from "../components/common/StatCard";
import { DataTable } from "../components/common/DataTable";
import { FilterBar } from "../components/common/FilterBar";
import { PageHeader } from "../components/common/PageHeader";
import { useDataFetching } from "../hooks/useDataFetching";
import { useFiltering } from "../hooks/useFiltering";
import { spacesAPI, campusAPI } from "../api";
import { QUERY_KEYS } from "../utils/constants";
import { formatDateTime } from "../utils/formatters";
import { Plus, Users, Calendar, Building2, Trash2, Edit, Eye, Grid3X3, List } from "lucide-react";
import { Space, Campus, FilterFunction } from "../types";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useNavigate } from "react-router-dom";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { MoreHorizontal } from "lucide-react";

export function SpacesPage() {
  const navigate = useNavigate();
  const [selectedCampusId, setSelectedCampusId] = useState<string>("");
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('grid');

  const {
    data: spacesData,
    isLoading,
    error,
    refetch
  } = useDataFetching(QUERY_KEYS.SPACES, {
    queryFn: () => spacesAPI.getSpaces({ 
      campus_id: selectedCampusId || undefined,
      is_deleted: false 
    }),
  });

  const { data: campusesData } = useDataFetching(QUERY_KEYS.CAMPUSES, {
    queryFn: campusAPI.getAllCampuses,
  });

  const spaces = spacesData?.items || [];

  // Handle different response structures for campuses
  let campuses: Campus[] = [];
  if (campusesData) {
    if (Array.isArray(campusesData)) {
      campuses = campusesData;
    } else if (campusesData.data && Array.isArray(campusesData.data)) {
      campuses = campusesData.data;
    } else if (campusesData.items && Array.isArray(campusesData.items)) {
      campuses = campusesData.items;
    } else if (campusesData.campuses && Array.isArray(campusesData.campuses)) {
      campuses = campusesData.campuses;
    }
  }

  const {
    searchTerm,
    setSearchTerm,
    filters,
    updateFilter,
    filteredData: filteredSpaces,
  } = useFiltering({
    data: spaces,
    searchFields: ['title', 'about', 'category'],
    filterFn: ((space: Space, filters) => {
      const campusMatch = filters.campusId
        ? typeof space.campusId === 'string' 
          ? space.campusId === filters.campusId
          : space.campusId._id === filters.campusId
        : true;
      const activeMatch = filters.isActive !== undefined
        ? space.is_active === filters.isActive
        : true;
      return campusMatch && activeMatch;
    }) as FilterFunction<Space>,
  });

  const activeSpaces = filteredSpaces.filter((space: Space) => space.is_active !== false).length;
  // const inactiveSpaces = filteredSpaces.filter((space: Space) => space.is_active === false).length;
  const totalMembers = filteredSpaces.reduce((sum: number, space: Space) => sum + (space.membersCount || 0), 0);
  const totalEvents = filteredSpaces.reduce((sum: number, space: Space) => sum + (space.eventIds?.length || 0), 0);

  const handleDelete = async (spaceId: string) => {
    if (confirm("Are you sure you want to delete this space?")) {
      try {
        await spacesAPI.deleteSpace(spaceId);
        refetch();
      } catch (error) {
        console.error("Failed to delete space:", error);
      }
    }
  };

  const handleRestore = async (spaceId: string) => {
    try {
      await spacesAPI.restoreSpace(spaceId);
      refetch();
    } catch (error) {
      console.error("Failed to restore space:", error);
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Space',
      render: (space: Space) => (
        <div className="flex items-center gap-3">
          {space.logoUrl && (
            <img 
              src={space.logoUrl} 
              alt={space.title}
              className="h-10 w-10 rounded-lg object-cover"
            />
          )}
          <div>
            <div className="font-medium">{space.title}</div>
            {space.category && (
              <div className="text-sm text-gray-500">{typeof space.category === 'string' ? space.category : space.category.name}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'campus',
      header: 'Campus',
      render: (space: Space) => {
        const campus = typeof space.campusId === 'object' ? space.campusId : null;
        return campus ? (
          <Badge variant="outline">
            <Building2 className="h-3 w-3 mr-1" />
            {campus.campus_name}
          </Badge>
        ) : '-';
      },
    },
    {
      key: 'members',
      header: 'Members',
      render: (space: Space) => (
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-gray-400" />
          <span>{space.membersCount || 0}</span>
          {space.requestsCount > 0 && (
            <Badge variant="secondary" className="ml-1">
              +{space.requestsCount} pending
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'events',
      header: 'Events',
      render: (space: Space) => (
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-400" />
          <span>{space.eventIds?.length || 0}</span>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (space: Space) => (
        <Badge variant={space.is_active !== false ? "default" : "secondary"} className={space.is_active !== false ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" : ""}>
          {space.is_active !== false ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      key: 'created',
      header: 'Created',
      render: (space: Space) => (
        <span className="text-sm text-gray-500">
          {formatDateTime(space.createdAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (space: Space) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => navigate(`/spaces/${space._id}`)}>
              <Eye className="h-4 w-4 mr-2" />
              View Details
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate(`/spaces/${space._id}/edit`)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </DropdownMenuItem>
            {space.is_deleted ? (
              <DropdownMenuItem onClick={() => handleRestore(space._id)}>
                Restore
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem 
                onClick={() => handleDelete(space._id)}
                className="text-red-600"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  if (isLoading) return <DashboardLayout><LoadingState /></DashboardLayout>;
  if (error) return <DashboardLayout><ErrorState message={error.message} /></DashboardLayout>;

  return (
    <DashboardLayout>
      <PageHeader
        title="Spaces"
        description="Manage campus organizations, clubs, and groups"
        actions={
          <div className="flex items-center gap-2">
            <div className="flex items-center rounded-md border border-input bg-background">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="rounded-r-none border-r"
              >
                <Grid3X3 className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'table' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('table')}
                className="rounded-l-none"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
            <Button onClick={() => navigate('/spaces/new')}>
              <Plus className="h-4 w-4 mr-2" />
              Create Space
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <StatCard
          title="Total Spaces"
          value={filteredSpaces.length}
          icon={<Building2 className="h-4 w-4" />}
          variant="blue"
          isLoading={isLoading}
        />
        <StatCard
          title="Active Spaces"
          value={activeSpaces}
          icon={<Building2 className="h-4 w-4" />}
          variant="green"
          isLoading={isLoading}
        />
        <StatCard
          title="Total Members"
          value={totalMembers}
          icon={<Users className="h-4 w-4" />}
          variant="purple"
          isLoading={isLoading}
        />
        <StatCard
          title="Total Events"
          value={totalEvents}
          icon={<Calendar className="h-4 w-4" />}
          variant="amber"
          isLoading={isLoading}
        />
      </div>

      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search spaces..."
      >
        <Select
          value={(filters.campusId as string) || ""}
          onValueChange={(value) => {
            const campusId = value === "all" ? undefined : value;
            updateFilter("campusId", campusId);
            setSelectedCampusId(value === "all" ? "" : value);
          }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Campuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Campuses</SelectItem>
            {campuses.map((campus: Campus) => (
              <SelectItem key={campus._id} value={campus._id}>
                {campus.campus_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.isActive === undefined ? "all" : String(filters.isActive)}
          onValueChange={(value) => {
            updateFilter("isActive", value === "all" ? undefined : value === "true");
          }}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="true">Active</SelectItem>
            <SelectItem value="false">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </FilterBar>

{viewMode === 'table' ? (
        <DataTable
          data={filteredSpaces}
          columns={columns}
          emptyMessage="No spaces found"
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredSpaces.map((space: Space) => (
            <div key={space._id} className="bg-white dark:bg-gray-800 rounded-lg border shadow-sm hover:shadow-md transition-shadow">
              <div className="relative">
                {space.coverImageUrl ? (
                  <img
                    src={space.coverImageUrl}
                    alt={space.title}
                    className="w-full h-32 object-cover rounded-t-lg"
                  />
                ) : (
                  <div className="w-full h-32 bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900 dark:to-purple-900 rounded-t-lg flex items-center justify-center">
                    <Building2 className="h-12 w-12 text-gray-400" />
                  </div>
                )}
                <div className="absolute top-2 right-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="secondary" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/spaces/${space._id}`)}>
                        <Eye className="h-4 w-4 mr-2" />
                        View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate(`/spaces/${space._id}/edit`)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleDelete(space._id)}
                        className="text-red-600"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              
              <div className="p-4">
                <div className="flex items-center gap-3 mb-2">
                  {space.logoUrl && (
                    <img
                      src={space.logoUrl}
                      alt={space.title}
                      className="h-10 w-10 rounded-full object-cover"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100 truncate">
                      {space.title}
                    </h3>
                    {space.category && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {typeof space.category === 'string' ? space.category : space.category.name}
                      </p>
                    )}
                  </div>
                </div>
                
                {space.about && (
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 line-clamp-2">
                    {space.about}
                  </p>
                )}
                
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      <span>{space.membersCount || 0}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      <span>{space.eventIds?.length || 0}</span>
                    </div>
                  </div>
                  <Badge variant={space.is_active !== false ? "default" : "secondary"}>
                    {space.is_active !== false ? "Active" : "Inactive"}
                  </Badge>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {space.category && (
                    <Badge variant="outline" className="text-xs">
                      {typeof space.category === 'string' ? space.category : space.category.name}
                    </Badge>
                  )}
                  {space.requestsCount > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      +{space.requestsCount} pending
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          ))}
          
          {filteredSpaces.length === 0 && (
            <div className="col-span-full text-center py-12">
              <Building2 className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-300">No spaces found</p>
            </div>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
